<template>
  <div class="audio-controls">
    <div class="controls">
      <div class="transport-cluster">
        <el-tooltip :content="labels.previousSubtitleTip" placement="top" :disabled="isMobile">
          <button
            class="control-btn control-btn--step"
            :aria-label="labels.previousSubtitle"
            @click="$emit('previous-subtitle')"
          >
            <img :src="icons.pastOne" alt="Previous" />
          </button>
        </el-tooltip>

        <el-tooltip :content="isPlaying ? labels.pauseTip : labels.playTip" placement="top" :disabled="isMobile">
          <button
            class="control-btn control-btn--play"
            :class="{ playing: isPlaying }"
            :aria-label="isPlaying ? labels.pause : labels.play"
            @click="$emit('toggle-play')"
          >
            <img :src="isPlaying ? icons.pause : icons.play" alt="Play/Pause" />
          </button>
        </el-tooltip>

        <el-tooltip :content="labels.nextSubtitleTip" placement="top" :disabled="isMobile">
          <button
            class="control-btn control-btn--step"
            :aria-label="labels.nextSubtitle"
            @click="$emit('next-subtitle')"
          >
            <img :src="icons.nextOne" alt="Next" />
          </button>
        </el-tooltip>
      </div>

      <div class="progress-container">
        <div class="time-display">
          <span>{{ formatTime(currentTime) }}</span>
          <div
            class="progress-bar-container"
            role="slider"
            tabindex="0"
            :aria-label="labels.seek"
            aria-valuemin="0"
            :aria-valuemax="Math.round(audioDuration)"
            :aria-valuenow="Math.round(currentTime)"
            :aria-valuetext="`${formatTime(currentTime)} / ${formatTime(audioDuration)}`"
            @click="$emit('progress-click', $event)"
            @keydown="seekByKeyboard"
            @mousemove="$emit('progress-hover', $event)"
            @mouseleave="$emit('hide-time-tooltip')"
          >
            <div class="progress-bar" :style="{ width: progressPercentage + '%' }"></div>
            <div class="progress-handle" :style="{ left: progressPercentage + '%' }" v-if="!isMobile"></div>
            <div class="tooltip" v-show="showTooltip && !isMobile" :style="{ left: tooltipPosition + 'px' }">
              {{ tooltipTime }}
            </div>
          </div>
          <span>{{ formatTime(audioDuration) }}</span>
        </div>
      </div>

      <div class="additional-controls playback-settings" :aria-label="labels.playbackSettings">
        <div class="setting-control font-size-control" :class="{ open: showFontSizeControl }">
          <el-tooltip :content="`${labels.fontSize} · ${fontSize}px`" placement="top" :disabled="isMobile || showFontSizeControl">
            <button
              class="setting-trigger"
              :aria-expanded="showFontSizeControl"
              :aria-label="`${labels.fontSize}，${fontSize}px`"
              @click="$emit('toggle-font-size-control')"
            >
              <span class="setting-glyph setting-glyph--font">Aa</span>
            </button>
          </el-tooltip>
          <div
            v-show="showFontSizeControl"
            class="setting-popover setting-popover--range"
            @mouseleave="$emit('hide-font-size-control')"
          >
            <div class="popover-head">
              <span>{{ labels.fontSize }}</span>
              <strong>{{ fontSize }}px</strong>
            </div>
            <p class="popover-help">{{ labels.fontSizeHelp }}</p>
            <input
              type="range"
              :aria-label="labels.fontSize"
              :min="minFontSize"
              :max="maxFontSize"
              :value="fontSize"
              class="setting-range font-size-range"
              :style="rangeStyle(fontSize, minFontSize, maxFontSize)"
              @input="$emit('set-font-size', Number($event.target.value))"
            />
            <div class="range-scale">
              <span>{{ minFontSize }}px</span>
              <span>{{ maxFontSize }}px</span>
            </div>
            <div class="popover-switch">
              <span>{{ labels.timestamps }}</span>
              <button
                type="button"
                role="switch"
                class="switch"
                :class="{ on: showTimestamps }"
                :aria-checked="showTimestamps"
                :aria-label="labels.timestamps"
                @click="$emit('toggle-timestamps')"
              >
                <span class="switch-thumb"></span>
              </button>
            </div>
          </div>
        </div>

        <div class="setting-control volume-control" :class="{ open: showVolumeControl }">
          <el-tooltip :content="`${labels.volume} · ${volumePercent}%`" placement="top" :disabled="isMobile || showVolumeControl">
            <button
              class="setting-trigger"
              :aria-expanded="showVolumeControl"
              :aria-label="`${labels.volume}，${volumePercent}%`"
              @click="$emit('toggle-volume-control')"
            >
              <span class="setting-glyph setting-glyph--volume" :class="{ muted: volumePercent === 0 }">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </el-tooltip>
          <div
            v-show="showVolumeControl"
            class="setting-popover setting-popover--range"
            @mouseleave="$emit('hide-volume-control')"
          >
            <div class="popover-head">
              <span>{{ labels.volume }}</span>
              <strong>{{ volumePercent }}%</strong>
            </div>
            <p class="popover-help">{{ labels.volumeHelp }}</p>
            <input
              type="range"
              :aria-label="labels.volume"
              min="0"
              max="100"
              :value="volume * 100"
              class="setting-range volume-range"
              :style="rangeStyle(volumePercent, 0, 100)"
              @input="$emit('set-volume', Number($event.target.value) / 100)"
            />
            <div class="range-scale">
              <span>0</span>
              <span>100</span>
            </div>
          </div>
        </div>

        <div class="setting-control playback-speed" :class="{ open: showSpeedOptions }">
          <el-tooltip :content="`${labels.speed} · ${playbackRate}×`" placement="top" :disabled="isMobile || showSpeedOptions">
            <button
              class="setting-trigger setting-trigger--speed"
              :aria-expanded="showSpeedOptions"
              :aria-label="`${labels.speed}，${playbackRate}倍`"
              @click="$emit('toggle-speed-options')"
            >
              <svg class="setting-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M4.2 17a9 9 0 1 1 15.6 0" />
                <path d="m12 14 4-5" />
                <circle cx="12" cy="14" r="1" fill="currentColor" stroke="none" />
              </svg>
            </button>
          </el-tooltip>
          <div
            v-show="showSpeedOptions"
            class="setting-popover setting-popover--speed"
            @mouseleave="$emit('hide-speed-options')"
          >
            <div class="popover-head">
              <span>{{ labels.speed }}</span>
              <strong>{{ playbackRate }}x</strong>
            </div>
            <p class="popover-help">{{ labels.speedHelp }}</p>
            <div class="speed-grid">
              <button
                v-for="rate in speedRates"
                :key="rate"
                class="setting-choice speed-option"
                :class="{ active: playbackRate === rate }"
                :aria-pressed="playbackRate === rate"
                @click="$emit('set-playback-rate', rate)"
              >
                {{ rate }}x
              </button>
            </div>
          </div>
        </div>

        <div class="setting-control playback-mode" :class="{ open: showModeOptions }">
          <el-tooltip :content="`${labels.playMode} · ${playModeName}`" placement="top" :disabled="isMobile || showModeOptions">
            <button
              class="setting-trigger"
              :aria-expanded="showModeOptions"
              :aria-label="`${labels.playMode}，${playModeName}`"
              @click="$emit('toggle-mode-options')"
            >
              <span class="setting-glyph setting-glyph--mode"></span>
            </button>
          </el-tooltip>
          <div
            v-show="showModeOptions"
            class="setting-popover setting-popover--mode"
            @mouseleave="$emit('hide-mode-options')"
          >
            <div class="popover-head">
              <span>{{ labels.playMode }}</span>
              <strong>{{ playModeName }}</strong>
            </div>
            <p class="popover-help">{{ labels.modeHelp }}</p>
            <button
              v-for="mode in playbackModes"
              :key="mode.key"
              class="setting-choice setting-choice--mode mode-option"
              :class="{ active: playMode === mode.key }"
              :aria-pressed="playMode === mode.key"
              :title="$t('audioPlayer.modeTips.' + mode.key)"
              @click="$emit('set-play-mode', mode.key)"
            >
              <span>{{ mode.label }}</span>
              <small>{{ mode.hint }}</small>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatTimeCompact } from '@/utils/format'

export default {
  name: 'PlayerAudioControls',
  emits: [
    'previous-subtitle',
    'toggle-play',
    'next-subtitle',
    'progress-click',
    'progress-hover',
    'hide-time-tooltip',
    'toggle-font-size-control',
    'hide-font-size-control',
    'set-font-size',
    'toggle-timestamps',
    'toggle-volume-control',
    'hide-volume-control',
    'set-volume',
    'toggle-speed-options',
    'hide-speed-options',
    'set-playback-rate',
    'toggle-mode-options',
    'hide-mode-options',
    'set-play-mode',
  ],
  props: {
    icons: { type: Object, required: true },
    isPlaying: { type: Boolean, default: false },
    showTimestamps: { type: Boolean, default: false },
    currentTime: { type: Number, default: 0 },
    audioDuration: { type: Number, default: 0 },
    progressPercentage: { type: Number, default: 0 },
    isMobile: { type: Boolean, default: false },
    showTooltip: { type: Boolean, default: false },
    tooltipPosition: { type: Number, default: 0 },
    tooltipTime: { type: String, default: '00:00' },
    showFontSizeControl: { type: Boolean, default: false },
    minFontSize: { type: Number, default: 12 },
    maxFontSize: { type: Number, default: 32 },
    fontSize: { type: Number, default: 16 },
    showVolumeControl: { type: Boolean, default: false },
    volume: { type: Number, default: 1 },
    playbackRate: { type: Number, default: 1 },
    showSpeedOptions: { type: Boolean, default: false },
    showModeOptions: { type: Boolean, default: false },
    playMode: { type: String, default: 'auto' },
  },
  computed: {
    playModeName() {
      return this.$t(`audioPlayer.${this.playMode}Mode`)
    },
    labels() {
      const isZh = String(this.$i18n?.locale || '').toLowerCase().startsWith('zh')
      return isZh
        ? {
            previousSubtitle: '上一句',
            play: '播放',
            pause: '暂停',
            nextSubtitle: '下一句',
            previousSubtitleTip: '上一句（← 或 ↑）',
            playTip: '播放（空格）',
            pauseTip: '暂停（空格）',
            nextSubtitleTip: '下一句（→ 或 ↓）',
            playbackSettings: '播放设置',
            fontSize: '字幕大小',
            fontSizeHelp: '调整练习区中的字幕字号',
            timestamps: '显示每句时间',
            volume: '音量',
            volumeHelp: '调整这段音频的播放音量',
            speed: '播放速度',
            speedHelp: '选择适合听力练习的语速',
            playMode: '播放模式',
            modeHelp: '选择播完一句后如何继续',
            continuous: '播完后自动播放下一句',
            sentencePractice: '播完一句后暂停',
            seek: '播放进度',
          }
        : {
            previousSubtitle: 'Previous sentence',
            play: 'Play',
            pause: 'Pause',
            nextSubtitle: 'Next sentence',
            previousSubtitleTip: 'Previous sentence (← or ↑)',
            playTip: 'Play (Space)',
            pauseTip: 'Pause (Space)',
            nextSubtitleTip: 'Next sentence (→ or ↓)',
            playbackSettings: 'Playback settings',
            fontSize: 'Subtitle size',
            fontSizeHelp: 'Adjust subtitle text in the practice area',
            timestamps: 'Show sentence times',
            volume: 'Volume',
            volumeHelp: 'Adjust the volume of this audio',
            speed: 'Playback speed',
            speedHelp: 'Choose a comfortable listening pace',
            playMode: 'Playback mode',
            modeHelp: 'Choose what happens after each sentence',
            continuous: 'Automatically play the next sentence',
            sentencePractice: 'Pause after each sentence',
            seek: 'Playback position',
          }
    },
    volumePercent() {
      return Math.round(Math.max(0, Math.min(1, this.volume || 0)) * 100)
    },
    speedRates() {
      return [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
    },
    playbackModes() {
      return [
        {
          key: 'auto',
          label: this.$t('audioPlayer.autoMode'),
          hint: this.labels.continuous,
        },
        {
          key: 'reading',
          label: this.$t('audioPlayer.readingMode'),
          hint: this.labels.sentencePractice,
        },
      ]
    },
  },
  mounted() {
    document.addEventListener('pointerdown', this.closeSettingsOutside)
    document.addEventListener('keydown', this.closeSettingsOnEscape)
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.closeSettingsOutside)
    document.removeEventListener('keydown', this.closeSettingsOnEscape)
  },
  methods: {
    closeSettingsOutside(event) {
      if (this.$el.querySelector('.playback-settings')?.contains(event.target)) return
      this.hideSettings()
    },
    closeSettingsOnEscape(event) {
      if (event.key === 'Escape') this.hideSettings()
    },
    hideSettings() {
      this.$emit('hide-font-size-control')
      this.$emit('hide-volume-control')
      this.$emit('hide-speed-options')
      this.$emit('hide-mode-options')
    },
    seekByKeyboard(event) {
      if (!this.audioDuration) return
      const increments = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5 }
      let targetTime
      if (event.key === 'Home') targetTime = 0
      else if (event.key === 'End') targetTime = this.audioDuration
      else if (event.key in increments) targetTime = this.currentTime + increments[event.key]
      else return
      event.preventDefault()
      const track = event.currentTarget
      const rect = track.getBoundingClientRect()
      const percentage = Math.max(0, Math.min(1, targetTime / this.audioDuration))
      this.$emit('progress-click', {
        currentTarget: track,
        clientX: rect.left + rect.width * percentage,
      })
    },
    formatTime(value) {
      return formatTimeCompact(value)
    },
    rangeStyle(value, min, max) {
      const numericValue = Number(value)
      const start = Number(min)
      const end = Number(max)
      const progress = end > start ? ((numericValue - start) / (end - start)) * 100 : 0
      return { '--range-progress': `${Math.max(0, Math.min(100, progress))}%` }
    },
  },
}
</script>

<style scoped>
.audio-controls {
  position: absolute;
  z-index: 20;
  left: 50%;
  bottom: max(12px, env(safe-area-inset-bottom));
  width: min(680px, calc(100% - 32px));
  height: 60px;
  padding: 0 16px;
  transform: translateX(-50%);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--color-surface-raised);
  box-shadow: var(--shadow-float);
}

@supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
  .audio-controls {
    background: var(--surface-floating-glass);
    backdrop-filter: blur(18px) saturate(140%);
    -webkit-backdrop-filter: blur(18px) saturate(140%);
  }
}

.audio-controls .controls {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  min-height: 58px;
  height: 58px;
  gap: 18px;
  padding: 0;
}

.transport-cluster,
.playback-settings {
  gap: 2px;
  padding: 0;
  border: 0;
  background: transparent;
}

.audio-controls .control-btn,
.setting-trigger {
  width: 36px;
  min-width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-pill);
  background: transparent;
  box-shadow: none;
}

.audio-controls .control-btn:hover,
.setting-trigger:hover,
.setting-control.open .setting-trigger {
  border: 0;
  background: var(--surface-active);
  color: var(--ink-primary);
  box-shadow: none;
  transform: none;
}

.audio-controls .control-btn--play {
  width: 42px;
  min-width: 42px;
  height: 42px;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--ink-primary);
  box-shadow: none;
}

.audio-controls .control-btn--play:hover,
.audio-controls .control-btn--play.playing {
  border: 0;
  background: var(--ink-primary);
  box-shadow: none;
}

.audio-controls .progress-container {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  min-width: 0;
  width: 100%;
  max-width: none;
  padding: 0;
}

.time-display {
  gap: 8px;
  color: var(--ink-tertiary);
}

.time-display span {
  min-width: 32px;
  font-family: var(--font-family-data);
  font-size: 10px;
  font-weight: 500;
}

.audio-controls .progress-bar-container,
.audio-controls .progress-bar {
  height: 3px;
}

.audio-controls .progress-bar-container {
  background: var(--border-default);
}

.audio-controls .progress-bar {
  background: var(--signal-active-strong);
}

.audio-controls .progress-handle {
  width: 9px;
  height: 9px;
  border: 2px solid var(--signal-active-strong);
  box-shadow: none;
  opacity: 0;
}

.audio-controls .progress-bar-container:hover .progress-handle {
  opacity: 1;
}

.setting-glyph {
  background: transparent;
  font-weight: 600;
}

.setting-icon {
  width: 20px;
  height: 20px;
}

.setting-popover {
  width: 248px;
  padding: 14px;
  border-color: var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  box-shadow: var(--shadow-float);
}

.popover-head {
  margin-bottom: 4px;
  color: var(--ink-primary);
  font-size: 13px;
  font-weight: 600;
}

.popover-head strong {
  font-family: var(--font-family-data);
  font-size: 12px;
  font-weight: 600;
}

.popover-help {
  margin: 0 0 14px;
  color: var(--ink-secondary);
  font-size: 12px;
  line-height: 1.5;
}

.popover-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--border-subtle);
  color: var(--ink-primary);
  font-size: 13px;
  font-weight: 500;
}

.switch {
  position: relative;
  flex: 0 0 auto;
  width: 34px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--border-default);
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.switch.on {
  background: var(--signal-active-strong);
}

.switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--surface-panel);
  box-shadow: 0 1px 2px rgba(16, 24, 32, 0.2);
  transition: transform 0.18s ease;
}

.switch.on .switch-thumb {
  transform: translateX(14px);
}

.setting-choice.active {
  border-color: var(--ink-primary);
  background: var(--ink-primary);
  box-shadow: none;
}

.audio-controls button:focus-visible,
.audio-controls input:focus-visible,
.progress-bar-container:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

@media (max-width: 720px) {
  .audio-controls {
    width: calc(100% - 20px);
    height: 58px;
    padding: 0 10px;
  }

  .audio-controls .controls {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas: none;
    min-height: 56px;
    height: 56px;
    gap: 6px;
  }

  .audio-controls .progress-container {
    display: flex;
    grid-area: auto;
  }

  .audio-controls .control-btn,
  .setting-trigger {
    width: 30px;
    min-width: 30px;
    height: 34px;
  }

  .audio-controls .control-btn--play {
    width: 38px;
    min-width: 38px;
    height: 38px;
  }

  .playback-settings {
    gap: 0;
  }

  .time-display span {
    display: none;
  }
}

@media (max-width: 480px) {
  .audio-controls .progress-container {
    position: absolute;
    left: 24px;
    right: 24px;
    bottom: 6px;
    width: auto;
  }

  .setting-popover {
    position: fixed;
    left: 50%;
    right: auto;
    bottom: calc(100% + 12px);
    width: min(280px, calc(100vw - 32px));
    transform: translateX(-50%);
  }

  .setting-popover::after {
    display: none;
  }

  .audio-controls .controls {
    grid-template-columns: auto auto;
    justify-content: space-between;
  }
}

@media (prefers-reduced-motion: reduce) {
  .audio-controls .progress-bar,
  .audio-controls .progress-handle,
  .switch,
  .switch-thumb {
    transition: none;
  }
}
</style>
